import { useCallback } from 'react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { useLazyQuery, useMutation, useApolloClient } from '@apollo/client/react';
import { getExpertoBannerPreference } from '../graphql/queries/getExpertoBannerPreference';
import { updateExpertoBannerPreference } from '../graphql/mutations/updateExpertoBannerPreference';
import { createExpertoBannerPreference } from '../graphql/mutations/createExpertoBannerPreference';

interface ExpertoBannerPreference {
  id: string;
  showBanner: boolean;
}

interface GetExpertoBannerPreferenceData {
  expertoBannerPreferences?: ExpertoBannerPreference;
}

interface GetExpertoBannerPreferenceVars {
  userId: string;
}

interface UpdateExpertoBannerPreferenceData {
  updateExpertoBannerPreference?: ExpertoBannerPreference;
}

interface UpdateExpertoBannerPreferenceVars {
  userId: string;
  preferenceId: string;
  showBanner: boolean;
}

interface CreateExpertoBannerPreferenceData {
  createExpertoBannerPreference?: ExpertoBannerPreference;
}

interface CreateExpertoBannerPreferenceVars {
  userId: string;
  showBanner: boolean;
}

const hasInternalServerError = (error: unknown): boolean =>
  CombinedGraphQLErrors.is(error) &&
  error.errors.some((graphQLError) => graphQLError.extensions?.code === 'INTERNAL_SERVER_ERROR');

export const useExpertoBannerPreference = () => {
  const client = useApolloClient();

  const [fetchPreference, { data: preferenceData }] =
    useLazyQuery<GetExpertoBannerPreferenceData, GetExpertoBannerPreferenceVars>(
      getExpertoBannerPreference,
      { client }
    );

  const [createPreference] =
    useMutation<CreateExpertoBannerPreferenceData, CreateExpertoBannerPreferenceVars>(
      createExpertoBannerPreference,
      { client }
    );

  const [updatePreference] =
    useMutation<UpdateExpertoBannerPreferenceData, UpdateExpertoBannerPreferenceVars>(
      updateExpertoBannerPreference,
      { client }
    );

  const fetchPreferenceWithFallback = useCallback(async (...args: Parameters<typeof fetchPreference>): Promise<void> => {
    try {
      const result = await fetchPreference(...args);

      if (result.error) {
        throw result.error;
      }
    } catch (error) {
      if (hasInternalServerError(error)) {
        const userId = args[0]?.variables?.userId;

        if (userId) {
          await createPreference({
            variables: {
              userId,
              showBanner: true,
            },
          });

          await fetchPreference(...args);
        }
      }

      throw error;
    }
  }, [createPreference, fetchPreference]);

  return {
    fetchPreference: fetchPreferenceWithFallback,
    showBanner: preferenceData?.expertoBannerPreferences?.showBanner === true,
    preferenceId: preferenceData?.expertoBannerPreferences?.id,
    updatePreference,
  };
};
